import React, { useEffect } from "react";
import { useToast } from "./ToastContext";

/**
 * ToastListener
 *
 * Listens for global `CustomEvent` named "app-toast" and forwards the payload to
 * the ToastContext `showToast` / `hideToast` / `clearToasts` methods.
 *
 * Expected event.detail shapes (examples):
 *  - { message: "Saved", severity: "success", duration: 3000 }
 *  - { action: "clear" }                // clears all toasts
 *  - { action: "hide", id: "toast_123" } // hide specific toast by id
 *
 * The component returns null (no UI) and should be mounted somewhere inside
 * the app (ideally beneath the ToastProvider). It assumes `useToast()` is
 * available from context.
 */
const ToastListener: React.FC = () => {
  const { showToast, hideToast, clearToasts } = useToast();

  useEffect(() => {
    const handler = (event: Event) => {
      try {
        const ce = event as CustomEvent<any>;
        const detail = ce?.detail;
        if (!detail) return;

        // Support explicit actions first
        if (detail.action === "clear") {
          clearToasts();
          return;
        }

        if (detail.action === "hide" && detail.id !== undefined) {
          hideToast(detail.id);
          return;
        }

        // Normal toast payload
        const message = detail.message ?? detail.msg ?? "";
        const severity = detail.severity ?? "info";
        const duration = detail.duration; // undefined means provider default
        const id = detail.id;

        // Ensure message is string
        showToast({
          message: String(message),
          severity: severity as any,
          duration: typeof duration === "number" ? duration : undefined,
          id,
        });
      } catch (err) {
        // Fail silently but log to console for debugging
        // do not throw - listener must be resilient
        // eslint-disable-next-line no-console
        console.warn("ToastListener: failed to handle app-toast event", err);
      }
    };

    window.addEventListener("app-toast", handler as EventListener);

    return () => {
      window.removeEventListener("app-toast", handler as EventListener);
    };
  }, [showToast, hideToast, clearToasts]);

  return null;
};

export default ToastListener;
